/**
 * Finding one instrument among ninety-two.
 *
 * ## What this replaces
 *
 * A `<select>` holding every root as a bare three-letter string, in feed order.
 * Sixty-eight of those are spot FX pairs, so a futures trader opening it landed
 * somewhere in the currency crosses and scrolled. On the phone the same list
 * was a horizontal chip strip you could swipe for a full second without
 * reaching the end.
 *
 * ## What it does instead
 *
 * Pinned symbols first, then a search field, then the catalogue grouped —
 * futures subdivided by what the contract is on, forex split into majors,
 * crosses and exotics. The grouping and the search ranking both live in
 * `@aegis/core` so the phone answers identically; this file is rendering and
 * keyboard behaviour and nothing else.
 *
 * ## The accessibility debt a custom picker takes on
 *
 * A `<select>` is operable by keyboard and announced correctly for free. Every
 * bit of that has to be rebuilt here or the change is a downgrade for anyone
 * not using a mouse: combobox semantics on the trigger, a listbox of options,
 * `aria-activedescendant` following the arrow keys, Enter to choose, Escape to
 * close, and focus returning to the trigger afterwards. That is most of the
 * code below, and it is the part a custom picker usually gets wrong.
 *
 * ## What it deliberately does not do
 *
 * It fetches no market data. `/v1/chart/bars` is what signals demand and
 * triggers analysis spend (ADR-0006), so a sparkline or a last price per row
 * would mean browsing the universe quietly spends model budget across all of
 * it. Rows show what an instrument IS, never what it is doing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, KeyboardEvent } from 'react';
import { buildCatalog, searchCatalog } from '@aegis/core';
import type { CatalogProduct } from '@aegis/core';
import type { Instrument } from '@aegis/api-client';
import { Icon } from './Icon.js';

export interface SymbolPickerProps {
  /** The root currently charted. */
  value: string;
  onChange(root: string): void;
  /** The master, from `/v1/instruments`. */
  instruments: readonly Instrument[];
  /** What the FEED carries, from `/health`. Anything else 404s. */
  available: readonly string[];
  /** Pinned roots in order, from `/v1/watchlist`. */
  pinned: readonly string[];
  onPinnedChange(next: string[]): void;
  /** The server's cap, so the control disables rather than round-trips a 400. */
  pinnedMax: number;
}

/** One flat row, whether it came from a group or from the search results. */
interface Row {
  readonly product: CatalogProduct;
  /** The size to select — differs from `product.root` when a micro matched. */
  readonly root: string;
}

export function SymbolPicker({
  value, onChange, instruments, available, pinned, onPinnedChange, pinnedMax,
}: SymbolPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  /**
   * Which size of a product is selected, remembered per product.
   *
   * A trader who works MES wants MES the next time they open ES, and having to
   * re-pick the size on every visit would make nesting worse than the flat list
   * it replaced.
   */
  const [size, setSize] = useState<Record<string, string>>({});

  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const catalog = useMemo(
    () => buildCatalog(instruments, new Set(available)),
    [instruments, available],
  );

  const matches = useMemo(() => searchCatalog(catalog, query), [catalog, query]);

  /** The pinned row, resolved against the catalogue and in the user's order. */
  const pinnedRows = useMemo<Row[]>(() => pinned.flatMap((root) => {
    const product = catalog.products.find(
      (p) => p.sizes.some((s) => s.root === root),
    );
    return product ? [{ product, root }] : [];
  }), [catalog, pinned]);

  /**
   * Every row the arrow keys can reach, in the order they are rendered.
   *
   * One list rather than one per section, because `aria-activedescendant` names
   * a single element and the arrow keys have to cross a heading without
   * stopping on it.
   */
  const rows = useMemo<Row[]>(() => {
    if (query) return matches.map((m) => ({ product: m.product, root: m.root }));
    return [
      ...pinnedRows,
      ...catalog.sections.flatMap((s) => s.groups.flatMap((g) => g.products.map(
        (p) => ({ product: p, root: size[p.root] ?? p.root }),
      ))),
    ];
  }, [query, matches, pinnedRows, catalog, size]);

  // Reset the highlight whenever the list changes underneath it, or the arrow
  // keys resume from wherever the previous list happened to leave them.
  useEffect(() => { setActive(0); }, [query]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    // Focus goes back where it came from. Without this it lands on <body> and
    // a keyboard user has to tab in from the top of the page again.
    triggerRef.current?.focus();
  }, []);

  const choose = useCallback((root: string, product: CatalogProduct) => {
    setSize((s) => ({ ...s, [product.root]: root }));
    onChange(root);
    close();
  }, [onChange, close]);

  // Dismiss on an outside click. Pointerdown rather than click so a drag that
  // starts outside and ends inside does not count as staying.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent): void => {
      if (!popRef.current?.contains(e.target as Node)
        && !triggerRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open, close]);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  // Keep the highlighted row on screen when the arrows walk past the fold.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`#sym-opt-${active}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (rows.length === 0 ? 0 : (a + 1) % rows.length));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (rows.length === 0 ? 0 : (a - 1 + rows.length) % rows.length));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[active];
      if (row) choose(row.root, row.product);
    }
  };

  const togglePin = (root: string): void => {
    onPinnedChange(pinned.includes(root)
      ? pinned.filter((r) => r !== root)
      : [...pinned, root]);
  };

  let index = -1;
  const nextIndex = (): number => { index += 1; return index; };

  const row = (r: Row, key: string): JSX.Element => {
    const i = nextIndex();
    const isPinned = pinned.includes(r.root);
    const capped = !isPinned && pinned.length >= pinnedMax;
    return (
      <div
        key={key}
        id={`sym-opt-${i}`}
        role="option"
        aria-selected={r.root === value}
        className={`sym-row ${i === active ? 'active' : ''} ${r.root === value ? 'on' : ''}`}
        onPointerEnter={() => setActive(i)}
        onClick={() => choose(r.root, r.product)}
      >
        <span className="sym-root mono">{r.root}</span>
        <span className="sym-desc">{r.product.description}</span>

        {/* Only when there is a choice to make. Most products have one size. */}
        {r.product.sizes.length > 1 && (
          <span className="sym-sizes" role="group" aria-label="Contract size">
            {r.product.sizes.map((s) => (
              <button
                key={s.root}
                type="button"
                className={`sym-size ${s.root === r.root ? 'on' : ''}`}
                aria-pressed={s.root === r.root}
                // The row's own click would choose and close, so a size button
                // has to stop it — picking a size is not picking the symbol.
                onClick={(e) => { e.stopPropagation(); setSize((m) => ({ ...m, [r.product.root]: s.root })); }}
                title={s.ratio ? `${s.label} — ${s.ratio}x` : s.label}
              >{s.label}</button>
            ))}
          </span>
        )}

        <button
          type="button"
          className={`sym-pin ${isPinned ? 'on' : ''}`}
          disabled={capped}
          aria-pressed={isPinned}
          aria-label={isPinned ? `Unpin ${r.root}` : `Pin ${r.root}`}
          title={capped ? `You can pin ${pinnedMax}. Unpin one first.` : undefined}
          onClick={(e) => { e.stopPropagation(); togglePin(r.root); }}
        ><Icon name="star" /></button>
      </div>
    );
  };

  return (
    <div className="sym-picker">
      <button
        ref={triggerRef}
        type="button"
        className="select sym-trigger"
        // The full combobox contract. A div with a click handler announces
        // nothing, and this is the control the whole screen hangs off.
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls="sym-listbox"
        aria-label="Symbol"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="mono">{value}</span>
        <Icon name="chevronDown" />
      </button>

      {open && (
        <div className="sym-pop" ref={popRef} onKeyDown={onKeyDown}>
          <input
            ref={inputRef}
            className="input sym-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search ES, gold, EURUSD…"
            aria-label="Search symbols"
            aria-controls="sym-listbox"
            aria-activedescendant={rows.length > 0 ? `sym-opt-${active}` : undefined}
            autoComplete="off"
            spellCheck={false}
          />

          <div className="sym-list" id="sym-listbox" role="listbox" aria-label="Symbols" ref={listRef}>
            {query ? (
              matches.length > 0
                ? matches.map((m) => row({ product: m.product, root: m.root }, `q:${m.root}`))
                : (
                  <p className="sym-empty dim">
                    Nothing matches “{query}”. Not every instrument AEGIS knows has a
                    live feed — the ones that do are listed when the search is empty.
                  </p>
                )
            ) : (
              <>
                {pinnedRows.length > 0 && (
                  <>
                    <div className="sym-head" role="presentation">Pinned</div>
                    {pinnedRows.map((r) => row(r, `pin:${r.root}`))}
                  </>
                )}
                {catalog.sections.map((section) => (
                  <div key={section.id}>
                    <div className="sym-section" role="presentation">
                      {section.label}<span className="dim"> {section.count}</span>
                    </div>
                    {section.groups.map((group) => (
                      <div key={group.id}>
                        <div className="sym-head" role="presentation">{group.label}</div>
                        {group.products.map((p) => row(
                          { product: p, root: size[p.root] ?? p.root }, `g:${p.root}`,
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
